/* ==========================================================================
   FK CRO — cart drawer

   Dawn's CartDrawer / CartDrawerItems, plus FKCartProgress: the live paint for
   the multi-tier reward bar rendered by snippets/fk-shipping-progress.liquid.

   How the bar stays live
   ----------------------
   Every cart mutation already re-renders the markup the bar lives in:
     • add to cart      -> CartDrawer#renderContents replaces #CartDrawer
     • qty / remove     -> CartItems#updateQuantity replaces .drawer__inner
     • external change  -> CartItems#onCartUpdate replaces <cart-drawer-items>
   So the server-rendered fill is always correct. FKCartProgress runs on top of
   that to (a) animate the fill from 0 to its target so the width transition is
   visible after a DOM swap, and (b) repaint immediately from the cart JSON of
   the change request, before the section HTML lands.
   ========================================================================== */

window.FKCartProgress = (function () {
  'use strict';

  function moneyFormat() {
    return (window.FKCRO && window.FKCRO.moneyFormat) || '${{amount}}';
  }

  function formatMoney(cents) {
    var value = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return moneyFormat().replace(/\{\{\s*amount[^}]*\}\}/, value);
  }

  function bars() {
    return document.querySelectorAll('[data-fk-cart-prog]');
  }

  /* Repaint one bar for a given cart total (in cents). */
  function paintBar(bar, total, animate) {
    var max = Number(bar.getAttribute('data-fk-prog-max') || 0);
    if (!max) return;

    var pct = Math.min((total / max) * 100, 100);
    if (pct < 0) pct = 0;

    var fill = bar.querySelector('[data-fk-prog-fill]');
    if (fill) {
      if (animate) {
        fill.style.transition = 'none';
        fill.style.width = '0%';
        // Force a reflow so the browser has a 0% frame to animate away from.
        void fill.offsetWidth;
        fill.style.transition = '';
      }
      fill.style.width = pct + '%';
    }

    var nextCents = 0;
    var nextReward = '';

    var markers = bar.querySelectorAll('[data-fk-prog-marker]');
    Array.prototype.forEach.call(markers, function (marker) {
      var cents = Number(marker.getAttribute('data-fk-tier-cents') || 0);
      var met = total >= cents;
      marker.classList.toggle('is-met', met);
      if (!met && !nextCents) {
        nextCents = cents;
        var reward = marker.querySelector('.fk-cart-prog__reward');
        nextReward = reward ? (reward.textContent || '').trim() : '';
      }
    });

    var label = bar.querySelector('[data-fk-prog-label]');
    if (label) {
      if (!nextCents) {
        label.innerHTML = bar.getAttribute('data-fk-ship-met') || 'REWARD UNLOCKED';
        bar.classList.add('fk-cart-prog--met', 'fk-ship--met');
      } else {
        var away = formatMoney(nextCents - total);
        label.innerHTML = "YOU'RE <em>" + away + '</em> AWAY FROM ' + nextReward;
        bar.classList.remove('fk-cart-prog--met', 'fk-ship--met');
      }
    }

    bar.setAttribute('data-fk-prog-total', String(total));
  }

  /* total is optional — each bar falls back to its own server-rendered total. */
  function paint(total, animate) {
    Array.prototype.forEach.call(bars(), function (bar) {
      var value =
        typeof total === 'number' && !isNaN(total)
          ? total
          : Number(bar.getAttribute('data-fk-prog-total') || 0);
      paintBar(bar, value, animate === true);
    });
  }

  function animate(total) {
    var run = function () {
      paint(total, true);
    };
    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(run);
    } else {
      setTimeout(run, 16);
    }
  }

  return { paint: paint, animate: animate };
})();

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      // Replay the reward bar fill on open so it reads as a live meter.
      if (window.FKCartProgress) window.FKCartProgress.animate();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      if (window.FKCartProgress) {
        window.FKCartProgress.animate(
          typeof parsedState.total_price === 'number' ? parsedState.total_price : undefined
        );
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/*
 * Any cart change published through Dawn's pubsub repaints every reward bar.
 * cartData carries the authoritative total, so the bar is correct even in the
 * window before the re-rendered section HTML is swapped in.
 */
(function () {
  'use strict';

  function onCartUpdate(event) {
    var total =
      event && event.cartData && typeof event.cartData.total_price === 'number'
        ? event.cartData.total_price
        : undefined;
    if (window.FKCartProgress) window.FKCartProgress.animate(total);
  }

  function boot() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
    }
    if (window.FKCartProgress) window.FKCartProgress.paint();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
